import { SearchProvider } from '@faststore/sdk'
import { useEffect } from 'react'
import { ITEMS_PER_PAGE } from 'src/constants'
import { applySearchState } from 'src/sdk/search/state'
import {
  useCategoryViewEvent,
  useDepartmentViewEvent,
} from 'src/utils/gtmEvents'
import type {
  CollectionPageQueryQuery,
  ServerCollectionPageQueryQuery,
  CollectionPageQueryQueryVariables,
} from '@generated/graphql'
import type { PageProps } from 'gatsby'
import type { SearchState } from '@faststore/sdk'
import type { SearchResult } from 'src/components/sections/ProductGalleryV2/types'
import type { PlacementResponse } from 'src/utils/placements/fetchPlacementData'
import { dynamicYieldContextPage } from 'src/utils/dynamicYield/dynamicYieldContextPage'
import ProductGallery from 'src/components/sections/ProductGalleryV2/index'
import { MusePlpEntryPoint } from 'src/components/muse/MusePlpEntryPoint'
import RenderPageSections from 'src/components/cms/RenderedPageSections'
import { ListingH1 } from 'src/components/plp/ListingH1'
import {
  splitSeoFaqSections,
  withListingHeading,
} from 'src/utils/plp/partitionPlpSections'
import type { ContextualFilter } from 'src/components/department/ContextualFilters'

type CmsData = {
  sections?: Array<{ name: string; data?: any }>
  seo?: {
    siteMetadataWithSlug?: {
      title?: string
      description?: string
    }
  }
} | null

type ServerData = {
  serverData: {
    searchParams: SearchState
    searchResult: SearchResult
    data: any
    placementData: PlacementResponse & { categoryName?: string }
    cmsData?: CmsData
    contextualFiltersData?: ContextualFilter[] | null
  }
  slug: string
  title: string
  type?: string
}

type Props = PageProps<
  CollectionPageQueryQuery,
  CollectionPageQueryQueryVariables,
  unknown,
  ServerCollectionPageQueryQuery
> &
  ServerData

export function CategoryPageTemplate(props: Props) {
  const {
    serverData,
    serverData: { placementData, cmsData, contextualFiltersData },
    title,
    type,
  } = props

  const { searchParams, searchResult } = serverData

  const categories =
    serverData.data?.collection?.breadcrumbList?.itemListElement?.map(
      (item: any) => item.name
    )

  useEffect(() => {
    if (!categories?.length) {
      return
    }

    dynamicYieldContextPage({
      page: 'CATEGORY',
      data: categories.map((category: string) => category),
    })
  }, [categories])

  useDepartmentViewEvent()
  useCategoryViewEvent()

  if (serverData === null) {
    return null
  }

  const {
    data: { collection },
  } = serverData

  const titlePage =
    collection?.breadcrumbList.itemListElement[
      collection?.breadcrumbList.itemListElement.length - 1
    ]

  const listingName = titlePage?.name ?? title ?? ''
  const sections = withListingHeading(cmsData?.sections ?? [], listingName)
  const { aboveGallery, belowGallery } = splitSeoFaqSections(sections)
  const hasActiveTopBanner = sections.some(
    (section) => section.data?.showListingH1
  )

  return (
    <SearchProvider
      onChange={applySearchState}
      itemsPerPage={ITEMS_PER_PAGE}
      {...searchParams}
    >
      {aboveGallery.length > 0 ? (
        <RenderPageSections
          sections={aboveGallery as Array<{ name: string; data: any }>}
        />
      ) : null}

      {!hasActiveTopBanner && listingName ? (
        <ListingH1 text={listingName} variant="visible" />
      ) : null}

      <ProductGallery
        title={listingName}
        type="category"
        dataSports={null}
        pageType={type}
        searchResult={searchResult}
        placementData={placementData}
        alignWithListingHeading={!hasActiveTopBanner && Boolean(listingName)}
        contextualFiltersData={contextualFiltersData}
      />
      <MusePlpEntryPoint />

      {belowGallery.length > 0 ? (
        <RenderPageSections
          sections={belowGallery as Array<{ name: string; data: any }>}
        />
      ) : null}
    </SearchProvider>
  )
}
